import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { CheckCircle2, ArrowRight, Sparkles, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogFooter } from "../ui/dialog";
import { formatCurrencyBRL } from "../../lib/utils";

export function UserFormSubmitSection({
  filteredProcesses,
  userData,
  selectedProcessIds,
  toggleProcessType,
  isSubmitting,
  handleFormSubmit,
  isModalOpen,
  setIsModalOpen,
  onSubmitSuccess,
  setCessao,
  submitCessao,
}) {
  const [selectedForCessao, setSelectedForCessao] = useState([]);
  const selectedProcesses = filteredProcesses.filter((p) => selectedProcessIds.includes(p.id));
  const selectedEligibleProcesses = selectedProcesses.filter(
    (p) => !Boolean(p.processo?.bloquear_cessao ?? p.bloquear_cessao),
  );
  const blockedSelectedProcesses = selectedProcesses.filter(
    (p) => Boolean(p.processo?.bloquear_cessao ?? p.bloquear_cessao),
  );
  const selectedEligibleIds = selectedEligibleProcesses.map((p) => p.id);

  const countToPortuguese = (n) => {
    const words = ["zero","um","dois","três","quatro","cinco","seis","sete","oito","nove","dez"];
    return words[n] ?? n;
  };

  useEffect(() => {
    if (isModalOpen) {
      setSelectedForCessao(selectedEligibleIds);
    }
  }, [isModalOpen, selectedEligibleIds]);

  const àVistaTotal = selectedEligibleProcesses.reduce(
    (sum, p) => sum + (Number(p.valor_proposta_final) || 0),
    0,
  );

  const creditoTotal = selectedEligibleProcesses.reduce(
    (sum, p) => sum + Number(p.valor_credito || p.valor_parcelado || 0),
    0,
  );

  const fgtsTotal = selectedEligibleProcesses.reduce(
    (sum, p) => sum + (Number(p.valor_fgts) || 0),
    0,
  );

  const buildDocTriggerPlan = (processId, tipoAceite) => {
    const pc = filteredProcesses.find((p) => p.id === processId);
    if (!pc) return { processId, tipoAceite, docs: [] };

    const allDocs = pc.processo?.documentos || [];
    const parentDocs = tipoAceite === "acordo"
      ? allDocs.filter((d) => (d.tipo || "").toLowerCase() === "termo" && !d.parent_id)
      : allDocs.filter((d) => ["cessao", "adesao"].includes((d.tipo || "").toLowerCase()) && !d.parent_id);

    return {
      processId,
      nomeProcesso: pc.processo?.nome_processo,
      tipoAceite,
      docs: parentDocs.map((d) => ({
        id: d.id,
        tipo: d.tipo,
        template_token: d.template_token,
        children: (allDocs.filter((c) => c.parent_id === d.id)).map((c) => ({
          id: c.id,
          tipo: c.tipo,
          template_token: c.template_token,
        })),
      })),
    };
  };

  const handleConfirmExplicitCessao = async () => {
    const triggerPlan = selectedProcessIds.map((id) => {
      const tipoAceite = selectedForCessao.includes(id) && selectedEligibleIds.includes(id)
        ? "acordo_cessao"
        : "acordo";
      return buildDocTriggerPlan(id, tipoAceite);
    });
    console.group("🔔 [ZapSign] Plano de disparo — handleConfirmExplicitCessao");
    console.table(triggerPlan.map((p) => ({
      nomeProcesso: p.nomeProcesso,
      tipoAceite: p.tipoAceite,
      parentDocs: p.docs.map((d) => `${d.tipo} (${d.template_token})`).join(", ") || "⚠️ nenhum",
      children: p.docs.flatMap((d) => d.children).map((c) => `${c.tipo} (${c.template_token})`).join(", ") || "nenhum",
    })));
    console.log("Detalhes completos:", triggerPlan);
    console.groupEnd();

    for (const id of selectedProcessIds) {
      const isNowCessao = selectedForCessao.includes(id) && selectedEligibleIds.includes(id);
      toggleProcessType(id, isNowCessao);
    }

    const hasAnyCessao = selectedForCessao.length > 0;
    setCessao(hasAnyCessao);

    if (hasAnyCessao) {
      const success = await submitCessao();
      if (success) {
        setIsModalOpen(false);
        onSubmitSuccess();
      }
    } else {
      setIsModalOpen(false);
      onSubmitSuccess();
    }
  };

  const handleConfirmSingleCessao = async (processId) => {
    const triggerPlan = selectedProcessIds.map((id) => {
      const tipoAceite = id === processId && selectedEligibleIds.includes(id)
        ? "acordo_cessao"
        : "acordo";
      return buildDocTriggerPlan(id, tipoAceite);
    });
    console.group("🔔 [ZapSign] Plano de disparo — handleConfirmSingleCessao");
    console.log("Processo escolhido para cessão:", processId);
    console.table(triggerPlan.map((p) => ({
      nomeProcesso: p.nomeProcesso,
      tipoAceite: p.tipoAceite,
      parentDocs: p.docs.map((d) => `${d.tipo} (${d.template_token})`).join(", ") || "⚠️ nenhum",
      children: p.docs.flatMap((d) => d.children).map((c) => `${c.tipo} (${c.template_token})`).join(", ") || "nenhum",
    })));
    console.log("Detalhes completos:", triggerPlan);
    console.groupEnd();

    // Enviar apenas esse processo como cessão
    for (const id of selectedProcessIds) {
      toggleProcessType(id, id === processId && selectedEligibleIds.includes(id));
    }

    setCessao(true);

    const success = await submitCessao();
    if (success) {
      setIsModalOpen(false);
      onSubmitSuccess();
    }
  };

  const handleConfirmParceladoAll = async () => {
    for (const id of selectedProcessIds) {
      toggleProcessType(id, false);
    }
    setCessao(false);
    setIsModalOpen(false);
    onSubmitSuccess();
  };

  return (
    <>
      <div className="mt-4 p-6 md:p-8 bg-slate-900/5 border border-slate-900/10 rounded-3xl relative overflow-hidden group/submit">
        <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none group-hover/submit:opacity-10 transition-opacity">
          <Sparkles className="w-24 h-24 text-slate-900" />
        </div>

        <div className="relative z-10 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-900/10 flex items-center justify-center border border-slate-900/10 group-hover/submit:bg-yellow-500/10 group-hover/submit:border-yellow-500/20 transition-colors">
                <CheckCircle2 className="w-5 h-5 text-slate-900 group-hover/submit:text-yellow-600 transition-colors" />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="bg-slate-900 text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter">
                    Passo Final
                  </span>
                  <h2 className="text-xl font-black text-slate-900 tracking-tight uppercase italic">
                    {filteredProcesses.length > 0
                      ? "Confirmação Final"
                      : "Salvar Cadastro"}
                  </h2>
                </div>
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest opacity-80">
                  Verifique os dados e clique no botão abaixo
                </p>
              </div>
            </div>
          </div>

          <Button
            size="lg"
            onClick={() => {
              if (filteredProcesses.length === 0) {
                handleFormSubmit("update_only");
              } else if (userData.falecido) {
                handleFormSubmit("falecido");
              } else {
                const hasJuri = selectedEligibleProcesses
                  .some((p) => Number(p.valor_proposta_final || 0) > 0);

                if (hasJuri) {
                  handleFormSubmit("acordo", () => setIsModalOpen(true));
                } else {
                  handleFormSubmit("acordo");
                }
              }
            }}
            disabled={isSubmitting}
            className="w-full h-auto py-5 flex flex-col items-center justify-center gap-1 bg-slate-900 hover:bg-slate-800 text-white font-bold text-base shadow-lg transition-all hover:scale-[1.01] active:scale-[0.99] group overflow-hidden relative"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-yellow-500"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Processando...
              </span>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-500" />
                  <span>
                    {filteredProcesses.length > 0
                      ? "Aceitar acordo e continuar"
                      : "Salvar dados cadastrais"}
                  </span>
                </div>
              </>
            )}
          </Button>
        </div>
      </div>

      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) {
            onSubmitSuccess();
          }
        }}
      >
        <DialogContent className="overflow-y-auto max-h-[92vh] w-full max-w-2xl bg-white border-none shadow-2xl p-0 rounded-2xl">
          <div className="relative">
            <div className="px-5 pt-3 pb-2">
              <p className="text-lg font-black text-slate-900 tracking-tight uppercase">
                PROPOSTA PARA ANTECIPAÇÃO DO ACORDO:
              </p>
            </div>

            <div className="px-5 pb-3 space-y-3">
              {blockedSelectedProcesses.length > 0 && (
                <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-bold text-amber-900">
                    A proposta à vista está indisponível para:
                  </p>
                  <p className="mt-1 text-sm text-amber-800">
                    {blockedSelectedProcesses
                      .map((p) => p.processo?.nome_processo)
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              )}

              <div className="bg-[#E6F9F0] rounded-2xl p-4 border border-[#B5EAD7] space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-[#B5EAD7]/30">
                  <div className="bg-[#1F543E] text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter">
                    Processos
                  </div>
                  <span className="text-sm font-black text-[#1F543E] italic leading-snug">
                    {"PROPOSTA PARA " +
                      selectedEligibleProcesses
                        .filter((p) => selectedForCessao.includes(p.id))
                        .map((p) => `${p.processo.nome_processo}`)
                        .join(" + ") || "Nenhum selecionado"}
                  </span>
                </div>

                <div className="bg-white rounded-xl border-2 border-[#B5EAD7] px-4 py-3 shadow-sm space-y-2">
                  <div className="flex items-center gap-2 opacity-60">
                    <Sparkles className="w-3 h-3 text-[#1F543E]" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[#1F543E]">
                      Valor à vista consolidado
                    </span>
                  </div>
                  <p className="text-xl font-black text-[#1F543E] tabular-nums tracking-tighter italic">
                    {formatCurrencyBRL(àVistaTotal)}
                  </p>
                  {fgtsTotal > 0 && (
                    <div className="border-t border-[#B5EAD7] pt-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black uppercase tracking-widest text-[#1F543E]/60">
                          + FGTS consolidado
                        </span>
                        <span className="text-sm font-black text-[#1F543E] tabular-nums">
                          {formatCurrencyBRL(fgtsTotal)}
                        </span>
                      </div>
                      <p className="text-sm font-bold text-[#1F543E] leading-snug">
                        Os valores de FGTS serão pagos diretamente em sua conta vinculada e não serão antecipados pela Juri. A cessão de créditos não inclui o FGTS.
                      </p>
                    </div>
                  )}
                </div>

                <ul className="space-y-1.5">
                  {[
                    "Sua participação no acordo já está confirmada. Os contratos serão enviados para assinatura em breve.",
                    "A proposta da Juri é opcional e independente do acordo — você não é obrigado a aderir.",
                    "Caso aceite a antecipação, você receberá o valor à vista sem necessidade de devolver qualquer quantia à Juri.",
                    "A decisão de antecipar seus créditos é individual e o STIUEG não se responsabiliza pelo desconto aplicado.",
                    "O documento do acordo é obrigatório para todos assinarem, independentemente da escolha sobre a antecipação.",
                  ].map((text, i) => (
                    <li
                      key={i}
                      className="flex gap-2 text-xs font-medium text-[#1F543E]/80 leading-snug"
                    >
                      <span className="text-[#1F543E] font-black shrink-0">
                        •
                      </span>
                      {text}
                    </li>
                  ))}
                </ul>

                {àVistaTotal > 0 && (
                  <Button
                    onClick={handleConfirmExplicitCessao}
                    disabled={isSubmitting || selectedForCessao.length === 0}
                    className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-black italic text-base rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-[0.98] transition-all"
                  >
                    {isSubmitting ? (
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                    ) : (
                      <>
                        <ArrowRight className="w-5 h-5" />
                        {selectedEligibleProcesses.length > 1
                          ? `QUERO RECEBER OS ${countToPortuguese(selectedEligibleProcesses.length).toUpperCase()} PROCESSOS À VISTA`
                          : "QUERO RECEBER À VISTA"}
                      </>
                    )}
                  </Button>
                )}
              </div>

              <div className="bg-[#E6F9F0] rounded-2xl p-4 border border-[#B5EAD7] space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-[#B5EAD7]/30">
                  <div className="bg-[#1F543E] text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter">
                    Ou escolha por processo
                  </div>
                </div>

                <div className="flex flex-wrap items-stretch gap-3">
                  {selectedEligibleProcesses
                    .map((p) => ({
                      ...p,
                      propostaAvista: Number(p.valor_proposta_final) || 0,
                      fgts: Number(p.valor_fgts) || 0,
                    }))
                    .sort((a, b) => b.propostaAvista - a.propostaAvista)
                    .map((process) => (
                      <div
                        key={process.id}
                        className="flex-1 min-w-[240px] flex flex-col bg-white rounded-xl border-2 border-[#B5EAD7] p-3 shadow-sm space-y-2"
                      >
                        <div className="space-y-1.5 flex-1">
                          <div className="flex items-center gap-2 opacity-60">
                            <Sparkles className="w-3 h-3 text-[#1F543E]" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-[#1F543E]">
                              {process.processo.nome_processo}
                            </span>
                          </div>
                          <p className="text-lg font-black text-[#1F543E] tabular-nums tracking-tighter italic">
                            {formatCurrencyBRL(process.propostaAvista)}
                          </p>
                          {process.fgts > 0 && (
                            <div className="border-t border-[#B5EAD7] pt-2 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#1F543E]/60">
                                  FGTS
                                </span>
                                <span className="text-xs font-black text-[#1F543E] tabular-nums">
                                  {formatCurrencyBRL(process.fgts)}
                                </span>
                              </div>
                              <p className="text-xs font-bold text-[#1F543E]/80 leading-snug">
                                Os valores de FGTS serão pagos diretamente em sua conta vinculada e não serão antecipados pela Juri. A cessão de créditos não inclui o FGTS.
                              </p>
                            </div>
                          )}
                        </div>

                        <Button
                          size="sm"
                          onClick={() => handleConfirmSingleCessao(process.id)}
                          disabled={isSubmitting}
                          className="h-9 w-full mt-auto text-[10px] font-black italic rounded-xl transition-all bg-emerald-600 hover:bg-emerald-500 text-white shadow-md shadow-emerald-500/20 active:scale-[0.98]"
                        >
                          {isSubmitting ? (
                            <span className="flex items-center gap-1">
                              <svg
                                className="animate-spin h-3 w-3 text-white"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                ></path>
                              </svg>
                              PROCESSANDO...
                            </span>
                          ) : (
                            <>
                              <ArrowRight className="w-3 h-3 mr-1" /> QUERO {process.processo.nome_processo.toUpperCase()} À VISTA!
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            <DialogFooter className="p-5 pt-2">
              <Button
                size="lg"
                onClick={handleConfirmParceladoAll}
                disabled={isSubmitting}
                className="w-full h-auto py-3 rounded-xl flex flex-col items-center transition-all duration-300 relative group active:scale-[0.99] border-b-4 bg-slate-900 border-slate-950 text-white hover:bg-slate-800"
              >
                <div className="flex flex-col items-center">
                  {isSubmitting ? (
                    <span className="flex items-center gap-2 font-bold text-lg italic">
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Processando...
                    </span>
                  ) : (
                    <>
                      <span className="text-base font-black italic tracking-tight uppercase">
                        Quero receber parcelado no processo
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest mt-0.5 opacity-60">
                        Confirmar Fluxo de Pagamento Judicial
                      </span>
                    </>
                  )}
                </div>
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
